import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import queryString from 'query-string'
import { withRouter } from 'react-router-dom'
import { Tabs, Tab, ProgressBar, Alert } from 'react-bootstrap';
import {
	getEmployeeRec, clearEmployeeRec, onFieldChange, onDashboardPermissionChange, onProfileImageUpload,
	saveEmployee, onAccessLevelsChanged, onEmployeeRemarksAdded, onEmployeeActiveStatusChange,
	onEmpLocationChange
} from '../../actions/adminAction/employeeActions';
import { getCommissionTypes } from '../../actions/adminAction/commissionActions';
import { validateUser } from '../../actions/adminAction/companyActions';
import BasicInfo from '../../components/adminComponents/employees/basicInfo/basicInfo';
import AdditionalInfo from '../../components/adminComponents/employees/additionalInfo/additionalInfo';
import AccessPermissions from '../../components/adminComponents/employees/accessLevel/accessPermissions';
import DashboardPermissions from '../../components/adminComponents/employees/dashboardPermissions/dashboardPermissions';
import { notify } from '../../util/notifications'
import { getAccessLevelsByModule, isManagement, isRetail } from '../../util/customMethods';
import { isValidForm } from '../../util/validationMethods'

// Constants
const PAGE_TITLE = 'Add New Employee';

const EMPLOYEE_FORM_INITIAL = {
	formBasicInfo: {},
	formAdvInfo: {}
}

const EMP_TABS = {
	TB_EMP_BASIC_INFO: 'TB_EMP_BASIC_INFO',
	TB_EMP_ADDITIONAL_INFO: 'TB_EMP_ADDITIONAL_INFO',
	TB_EMP_ACCESS_LEVEL: 'TB_EMP_ACCESS_LEVEL',
	TB_EMP_DASHBOARD_PERMISSIONS: 'TB_EMP_DASHBOARD_PERMISSIONS'
};

// Navigation flow based on user type
const getEmployeeNavigationFlow = () => {
	const isManagementUser = isManagement();
	const isRetailUser = isRetail();
	
	if (isManagementUser || isRetailUser) {
		return [EMP_TABS.TB_EMP_BASIC_INFO, EMP_TABS.TB_EMP_ADDITIONAL_INFO, EMP_TABS.TB_EMP_ACCESS_LEVEL, EMP_TABS.TB_EMP_DASHBOARD_PERMISSIONS];
	}
	return [EMP_TABS.TB_EMP_BASIC_INFO, EMP_TABS.TB_EMP_ACCESS_LEVEL];
};

class EmployeesRecPage extends Component {
	constructor(props) {
		super(props);
		this.state = {
			tabKey: EMP_TABS.TB_EMP_BASIC_INFO,
			eid: 0,
			isEmailExits: true,
			isLoading: false,
			loadingMessage: '',
			loadingProgress: 0
		}
		
		this.employeeForm = Object.assign({}, EMPLOYEE_FORM_INITIAL);
		this.navigationFlow = getEmployeeNavigationFlow();
		
		// Feature flags for debugging
		this.enableProgressBars = true; // Set to false to disable progress bars
	}

	componentWillMount = () => {
		const queryValues = queryString.parse(this.props.location.search);
		const employeeId = parseInt(queryValues.eid) || 0;
		this.setState({ eid: employeeId }, () => this.initEmpForm(employeeId));
	}

	componentDidUpdate(prevProps) {
		// Handle route changes - if eid changes, reinitialize form
		const prevQuery = queryString.parse(prevProps.location.search);
		const currentQuery = queryString.parse(this.props.location.search);
		const prevEid = parseInt(prevQuery.eid) || 0;
		const currentEid = parseInt(currentQuery.eid) || 0;
		
		if (prevEid !== currentEid) {
			this.setState({ eid: currentEid }, () => this.initEmpForm(currentEid));
		}
	}

	// Progress bar helper methods with error safety
	setLoadingState = (isLoading, message = '', progress = 0) => {
		try {
			this.setState({
				isLoading,
				loadingMessage: message,
				loadingProgress: progress
			});
		} catch (error) {
			console.warn('Failed to set loading state:', error);
			// Fallback to just set loading without progress
			this.setState({ isLoading });
		}
	}

	simulateProgress = (callback) => {
		this.setState({ loadingProgress: 25 });
		
		setTimeout(() => {
			this.setState({ loadingProgress: 50 });
		}, 200);
		
		setTimeout(() => {
			this.setState({ loadingProgress: 75 });
		}, 400);
		
		setTimeout(() => {
			this.setState({ loadingProgress: 100 });
			callback();
		}, 600);
	}

	initEmpForm = (id) => {
		const { dispatch } = this.props;
		
		// Clear Redux state
		dispatch(clearEmployeeRec());
		
		// Reset form references to ensure clean state
		this.employeeForm = Object.assign({}, EMPLOYEE_FORM_INITIAL);
		
		// Reset component state for new employees
		if (id === 0) {
			this.setState({
				tabKey: EMP_TABS.TB_EMP_BASIC_INFO,
				isEmailExits: true,
				isLoading: false,
				loadingMessage: '',
				loadingProgress: 0
			});
		} else {
			this.getEmpRecordById(id);
		}
	}

	getEmpRecordById = async (id) => {
		const { dispatch } = this.props;
		
		this.setLoadingState(true, 'Loading employee information...', 50);
		
		try {
			await dispatch(getCommissionTypes());
			await dispatch(getEmployeeRec(parseInt(id)));
			this.setLoadingState(true, 'Employee loaded successfully!', 100);
			
			setTimeout(() => {
				this.setLoadingState(false);
				this.forceUpdate();
			}, 1000);
		} catch (error) {
			this.setLoadingState(false);
			notify.createNotification('error', 'Failed to load employee information.', this.props.refNotify);
		}
	}

	onFieldValueChange = (key, value) => {
		const { dispatch } = this.props;
		dispatch(onFieldChange(key, value));
	}

	emailExits = async (dname) => {
		this.setLoadingState(true, 'Validating email...', 30);
		
		try {
			let status = await validateUser(0, dname, 'email');
			this.setState({ isEmailExits: status });
			this.setLoadingState(true, 'Email validation complete!', 100);
			
			setTimeout(() => {
				this.setLoadingState(false);
			}, 500);
		} catch (error) {
			this.setLoadingState(false);
			notify.createNotification('error', 'Failed to validate email.', this.props.refNotify);
		}
	}

	onEmpLocationChange = (empObj) => {
		const { dispatch } = this.props;
		dispatch(onEmpLocationChange(empObj));
	}

	// Consolidated form validation method
	validateAllForms = () => {
		const { formBasicInfo, formAdvInfo } = this.employeeForm;
		const isManagementUser = isManagement();
		const isRetailUser = isRetail();
		
		// Safely call validateAll methods only if forms are initialized
		if (formBasicInfo && typeof formBasicInfo.validateAll === 'function') {
			formBasicInfo.validateAll();
		}
		if (formAdvInfo && typeof formAdvInfo.validateAll === 'function' && (isManagementUser || isRetailUser)) {
			formAdvInfo.validateAll();
		}

		// Enhanced validation check - only validate forms that exist and are required
		const isBasicInfoValid = !formBasicInfo || isValidForm(formBasicInfo);
		const isAdvInfoValid = !formAdvInfo || isValidForm(formAdvInfo);

		return {
			isBasicInfoValid,
			isAdvInfoValid
		};
	}

	onSubmitEmployee = async () => {
		const { dispatch } = this.props;
		const { isBasicInfoValid, isAdvInfoValid } = this.validateAllForms();
		const isManagementUser = isManagement();
		const isRetailUser = isRetail();
		const showAdvancedTabs = isManagementUser || isRetailUser;

		// Enhanced validation logic - only validate required forms based on user type
		if (!isBasicInfoValid && this.employeeForm.formBasicInfo) {
			this.setState({ tabKey: EMP_TABS.TB_EMP_BASIC_INFO });
			notify.createNotification('error', 'Please complete the Basic Information section.', this.props.refNotify);
			return;
		}

		if (!isAdvInfoValid && showAdvancedTabs && this.employeeForm.formAdvInfo) {
			this.setState({ tabKey: EMP_TABS.TB_EMP_ADDITIONAL_INFO });
			notify.createNotification('error', 'Please complete the Additional Information section.', this.props.refNotify);
			return;
		}

		// Start loading with progress bar
		this.setLoadingState(true, 'Saving employee information...', 0);
		
		this.simulateProgress(async () => {
			try {
				const res = await dispatch(saveEmployee(this.props.empRec));
				if (res.statusCode === 200) {
					this.setLoadingState(true, 'Employee saved successfully! Redirecting...', 100);
					notify.createNotification('success', res.statusMsg, this.props.refNotify);
					
					// Clear form data after successful save
					this.clearAllFormData();
					
					setTimeout(() => {
						this.setLoadingState(false);
						this.props.history.push('/admin/employees');
					}, 1000);
				} else {
					this.setLoadingState(false);
					notify.createNotification('error', res.statusMsg, this.props.refNotify);
				}
			} catch (error) {
				this.setLoadingState(false);
				notify.createNotification('error', 'An error occurred while saving the employee.', this.props.refNotify);
			}
		});
	}

	// Clear all form data and reset to initial state
	clearAllFormData = () => {
		// Reset form references
		this.employeeForm = Object.assign({}, EMPLOYEE_FORM_INITIAL);
		
		// Clear any form validation states
		Object.keys(this.employeeForm).forEach(formKey => {
			if (this.employeeForm[formKey] && typeof this.employeeForm[formKey].reset === 'function') {
				this.employeeForm[formKey].reset();
			}
		});
		
		// Force re-render to ensure forms are cleared
		this.forceUpdate();
	}

	checkViewAccess = (accessLevel = {}) => {
		if (accessLevel.basicAccess?.view !== true) {
			this.props.history.push('/admin/notfound');
		}
	}

	permissionTabStatus = () => {
		if (isManagement() || isRetail())
			return ((this.state.eid || 0) != 0);
		else
			return true;
	}

	// Handle profile image upload - simplified to preserve original behavior
	handleProfileImageUpload = (profileFile) => {
		// Optional progress indication
		this.setLoadingState(true, 'Uploading profile image...', 50);
		
		// Use original dispatch pattern
		this.props.dispatch(onProfileImageUpload(profileFile));
		
		// Simple progress completion after a short delay
		setTimeout(() => {
			this.setLoadingState(false);
		}, 1500);
	}

	// Handle employee status change - preserve original behavior exactly
	handleEmployeeActiveStatusChange = (eid, activeStatus) => {
		// Use exactly the same pattern as original
		this.props.dispatch(onEmployeeActiveStatusChange(eid, activeStatus));
	}

	// Handle access level changes - simplified to match original behavior
	handleAccessLevelChange = async (module, group, page) => {
		// Optional progress indication
		this.setLoadingState(true, 'Updating access permissions...', 75);
		
		try {
			// Use original logic pattern
			let testObj = this.props.empRec.empAccess;
			let idx = testObj[module][group].findIndex(_page => {
				return _page.moduleId === page.moduleId;
			});
			
			if (idx !== -1) {
				testObj[module][group][idx] = page;
			}
			
			// Dispatch and wait if it returns a promise
			const result = this.props.dispatch(onAccessLevelsChanged(testObj));
			if (result && typeof result.then === 'function') {
				await result;
			}
			
			this.setLoadingState(false);
			this.forceUpdate();
		} catch (error) {
			this.setLoadingState(false);
			notify.createNotification('error', 'Failed to update permissions.', this.props.refNotify);
			console.error('Access level change error:', error);
		}
	}

	// Handle bulk access level changes - simplified
	handleAccessLevelAllChange = async (accessObj) => {
		// Optional progress indication
		this.setLoadingState(true, 'Updating all permissions...', 75);
		
		try {
			// Dispatch and wait if it returns a promise
			const result = this.props.dispatch(onAccessLevelsChanged(accessObj));
			if (result && typeof result.then === 'function') {
				await result;
			}
			
			this.setLoadingState(false);
			this.forceUpdate();
		} catch (error) {
			this.setLoadingState(false);
			notify.createNotification('error', 'Failed to update permissions.', this.props.refNotify);
			console.error('Access level all change error:', error);
		}
	}

	// Get next tab in navigation flow
	getNextTab = (currentTab) => {
		const currentIndex = this.navigationFlow.indexOf(currentTab);
		return currentIndex < this.navigationFlow.length - 1 
			? this.navigationFlow[currentIndex + 1] 
			: currentTab;
	}

	render() {
		const accessLevelObj = getAccessLevelsByModule('Employee');
		const { empRec } = this.props;
		const isManagementUser = isManagement();
		const isRetailUser = isRetail();
		const showAdvancedTabs = isManagementUser || isRetailUser;
		
		document.title = this.state.eid === 0 ? PAGE_TITLE : `${empRec.employeeInformation.firstName} ${empRec.employeeInformation.lastName}`;
		this.checkViewAccess(accessLevelObj);

		return (
			<Fragment>
				<div className="page-header">
					<div className="row mb-10">
						<div className="col-md-12 col-sm-12">
							<div className="title">
								<h4>
									<i className="fa fa-user"></i>
									{this.state.eid === 0 ? ` ${PAGE_TITLE}` : ` Edit ${empRec.employeeInformation.firstName} ${empRec.employeeInformation.lastName}`}
								</h4>
							</div>
						</div>
					</div>
				</div>

				{/* Progress Bar */}
				{this.state.isLoading && (
					<div className="pd-10 mb-15">
						<Alert variant="info" className="mb-10">
							<div className="d-flex align-items-center">
								<i className="fa fa-spinner fa-spin mr-10"></i>
								<span>{this.state.loadingMessage}</span>
							</div>
						</Alert>
						<ProgressBar 
							now={this.state.loadingProgress} 
							label={`${this.state.loadingProgress}%`}
							variant="success"
							striped
							animated
						/>
					</div>
				)}

				<div className="pd-10 bg-white border-radius-4 box-shadow container-box"
					 style={{ opacity: this.state.isLoading ? 0.7 : 1, pointerEvents: this.state.isLoading ? 'none' : 'auto' }}>
					<Tabs
						id="tab-employee"
						className="tab"
						activeKey={this.state.tabKey}
						onSelect={key => this.setState({ tabKey: key })}
					>
						{/* Basic Information Tab */}
						<Tab
							eventKey={EMP_TABS.TB_EMP_BASIC_INFO}
							title={<span><i className="fa fa-info-circle" style={{ display: 'block', textAlign: 'center' }}></i>Basic Information</span>}
						>
							<BasicInfo
								states={this.props.states || []}
								locations={this.props.locations || []}
								accessLevel={accessLevelObj || {}}
								eid={this.state.eid}
								empRec={empRec}
								onFieldValueChange={(key, value) => {
									this.onFieldValueChange(key, value);
								}}
								emailExits={(email) => { this.emailExits(email); }}
								isEmailExits={this.state.isEmailExits}
								onEmpLocationChange={(empObj) => {
									this.onEmpLocationChange(empObj);
								}}
								onSubmitBasicInfo={(e) => {
									const nextTab = showAdvancedTabs ? EMP_TABS.TB_EMP_ADDITIONAL_INFO : EMP_TABS.TB_EMP_ACCESS_LEVEL;
									this.setState({ tabKey: nextTab });
								}}
								onProfileImageUpload={this.handleProfileImageUpload}
								initCallBack={(frm) => {
									this.employeeForm.formBasicInfo = frm;
								}}
								disabled={this.state.isLoading}
								isNewEmployee={this.state.eid === 0}
								clearFormData={this.clearAllFormData}
							/>
						</Tab>

						{/* Additional Information Tab - Only for Management/Retail */}
						{showAdvancedTabs && (
							<Tab
								eventKey={EMP_TABS.TB_EMP_ADDITIONAL_INFO}
								title={<span><i className="fa fa-info-circle" style={{ display: 'block', textAlign: 'center' }}></i>Additional Information</span>}
							>
								<AdditionalInfo
									accessLevel={accessLevelObj || {}}
									states={this.props.states}
									departments={this.props.department}
									showEmpActiveStatus={this.state.eid == '0' ? false : true}
									licenceTypes={this.props.licenceTypes}
									empRec={empRec}
									commissionTypes={this.props.commissionTypes}
									onFieldValueChange={(key, value) => {
										this.onFieldValueChange(key, value);
									}}
									onUserRemarksAdded={(e, userRemarkObj) => {
										this.props.dispatch(onEmployeeRemarksAdded(userRemarkObj));
									}}
									onActiveStatusChange={(eid, activeStatus) => {
										// Fallback to original behavior if needed
										if (this.state.isLoading) {
											// Prevent multiple rapid clicks during loading
											return;
										}
										this.handleEmployeeActiveStatusChange(eid, activeStatus);
									}}
									onSubmitAdditionalInfo={(e) => {
										this.setState({ tabKey: EMP_TABS.TB_EMP_ACCESS_LEVEL });
									}}
									initCallBack={(frm) => {
										this.employeeForm.formAdvInfo = frm;
									}}
									disabled={this.state.isLoading}
								/>
							</Tab>
						)}

						{/* Access Permissions Tab */}
						<Tab
							eventKey={EMP_TABS.TB_EMP_ACCESS_LEVEL}
							title={<span><i className="fa fa-info-circle" style={{ display: 'block', textAlign: 'center' }}></i>Permissions</span>}
						>
							<AccessPermissions 
								accessLevel={empRec.empAccess} 
								eid={this.state.eid}
								onAccessLevelChanged={this.handleAccessLevelChange}
								onSubmitAccessLevel={(e) => {
									if (!isManagementUser && !isRetailUser) {
										this.onSubmitEmployee();
									} else {
										this.setState({ tabKey: EMP_TABS.TB_EMP_DASHBOARD_PERMISSIONS });
									}
								}}
								onAccessLevelAllChanged={this.handleAccessLevelAllChange}
								disabled={this.state.isLoading}
							/>
						</Tab>

						{/* Dashboard Permissions Tab - Only for Management/Retail */}
						{showAdvancedTabs && (
							<Tab
								eventKey={EMP_TABS.TB_EMP_DASHBOARD_PERMISSIONS}
								title={<span><i className="fa fa-info-circle" style={{ display: 'block', textAlign: 'center' }}></i>Dashboard Permissions</span>}
							>
								<DashboardPermissions
									accessLevel={accessLevelObj || {}}
									empRec={empRec}
									onItemPermissionChange={(item) => {
										this.props.dispatch(onDashboardPermissionChange(item));
									}}
									onSubmitEmployee={(e) => {
										this.onSubmitEmployee();
									}}
									disabled={this.state.isLoading}
								/>
							</Tab>
						)}
					</Tabs>
				</div>
			</Fragment>
		);
	}
}

const mapStateToProps = (state, ownProps) => {
	return {
		authInfo: state.authInfo,
		refNotify: state.refNotify,
		department: state.department.departmentList.departments,
		states: state.states.stateList,
		locations: state.location.locationList,
		empRec: state.employees.employeeRec,
		licenceTypes: state.licenseTypes.licenseTypeList,
		commissionTypes: state.commission.commissionTypes
	}
};

export default withRouter(connect(mapStateToProps)(EmployeesRecPage));